import {useMediaQuery} from '@chakra-ui/react';
import {unwrapResult} from '@reduxjs/toolkit';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  Col,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {QUERY_MOBILE} from '~/constants';
import {METHOD_KEYS, STI_TELECONSULTATION_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {setQuestionnaireAnswer, setUserQuestionnaireId} from '~/redux/questionnaires/slice';
import {addQuestionnaire} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {getCurrentUserFromStorage} from '~/services/auth';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';

const Questionnaire5Screen = () => {
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);
  const history = useHistory();
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const dispatch: AppDispatch = useDispatch();
  const {addMessage, addValidationMessage} = useNotification(dispatch);

  const {loading} = useRequestState(addQuestionnaire);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[STI_TELECONSULTATION_KEYS.MORE_INFORMATION]?.data,
  );

  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const {wpAuthUser} = medicalQuestionnaire;

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === STI_TELECONSULTATION_KEYS.MORE_INFORMATION);

  const rules = {
    medicationsTaking: (attributes: any) => {
      return {
        presence: {allowEmpty: false, message: `^This field can't be blank`},
        length: {
          minimum: 5,
          tooShort: '^This field is too short (minimum is %{count} characters)',
          tokenizer: function (value: any) {
            return trimSpaces(value);
          },
        },
      };
    },
  };
  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      medicationsTaking: currentAnswer?.medicationsTaking || '',
    },
    {rules},
  );

  const onClickSubmit = async () => {
    const errors = validation.getErrors();
    if (errors) {
      return;
    }

    const questionnaireAnswer = {
      key: STI_TELECONSULTATION_KEYS.MORE_INFORMATION,
      index: questionnaire?.index || '',
      data: {medicationsTaking: trimSpaces(inputs.medicationsTaking)},
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));

    const resAction = await dispatch(
      addQuestionnaire({
        data: {
          data: {
            ...medicalQuestionnaireAnswer,
            [STI_TELECONSULTATION_KEYS.MORE_INFORMATION]: {...questionnaireAnswer},
          },
          wpUserId: wpAuthUser?.wpUserId,
          methodKey: METHOD_KEYS.STI_TELECONSULTATION,
        },
      }),
    );

    if (requestOK(resAction, addQuestionnaire)) {
      const result = unwrapResult(resAction);
      if (result?.booking) {
        const type = result?.slotTime;
        dispatch(setUserQuestionnaireId({id: result?.medical?.id}));
        history.push(`/appointment?type=${type}&methodKey=${METHOD_KEYS.STI_TELECONSULTATION}&token=${savedToken}`);
      }
    }
  };

  return (
    <>
      <UiQuestionnaireLayout
        backIcon
        disableNext={!inputs.medicationsTaking}
        handleOnNext={onClickSubmit}
        actions={questionnaire?.actions}
        loading={loading}
        checkoutStep={questionnaire?.checkoutStep}
      >
        <Col alignItems="center" textAlign="center">
          <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.small} />
          <UiText fontSize="xl" fontWeight="bold" mt={spacing.medium}>
            {questionnaire?.questions?.default}
          </UiText>
          <UiFormControl id="medicationsTaking" isRequired isInvalid={!!validation?.errors?.medicationsTaking}>
            <UiTextarea
              mt={spacing.small}
              minH="300px"
              bg="white"
              borderRadius={spacing.tiny}
              placeholder={questionnaire?.elementType?.placeholder}
              defaultValue={inputs.medicationsTaking}
              onChange={({target}) => setInputs({medicationsTaking: target.value})}
            />
            {validation?.errors?.medicationsTaking && (
              <UiFormErrorMessage>{validation?.errors?.medicationsTaking[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
        </Col>
      </UiQuestionnaireLayout>
    </>
  );
};

export default Questionnaire5Screen;
